/**
 * SmartSidebar theme and styles — shared across Leads, Bookings, Inventory, etc.
 * Uses the same palette as smart_table_helper; sidebar body uses table bodyBg (#fafcfb).
 */

import { TABLE_THEME } from './smart_table_helper';

// ─── Sidebar theme (aligned with TABLE_THEME + sidebar-specific) ─────────────
export const SIDEBAR_THEME = {
  ...TABLE_THEME,
  /** Sidebar outer container (optional; often white) */
  sidebarBg: TABLE_THEME.white,
  /** Inner content area — same light color as table body */
  bodyBg: TABLE_THEME.bodyBg,
  /** Header label (e.g. "CONFIGURATIONS", "Pipeline") */
  headerColor: TABLE_THEME.muted,
  /** Active/selected item background */
  activeBg: '#ebf7f0',
  /** Active item left border and text accent */
  activeColor: TABLE_THEME.green,
};

/**
 * Resolve theme: use custom or SIDEBAR_THEME.
 */
export const getSidebarTheme = (theme) => theme || SIDEBAR_THEME;

// ─── Style getters ─────────────────────────────────────────────────────────
export function getSidebarContainerStyle(theme, options = {}) {
  const resolvedTheme = getSidebarTheme(theme);
  const { width = 220, bodyBg = true } = options;
  return {
    width: typeof width === 'number' ? width : 220,
    flexShrink: 0,
    background: bodyBg ? resolvedTheme.bodyBg : resolvedTheme.sidebarBg,
    borderRight: `1.5px solid ${resolvedTheme.border}`,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  };
}

export function getSidebarHeaderStyle(theme) {
  const resolvedTheme = getSidebarTheme(theme);
  return {
    padding: '16px 16px 10px',
  };
}

export function getSidebarHeaderLabelStyle(theme) {
  const resolvedTheme = getSidebarTheme(theme);
  return {
    fontSize: 10.5,
    fontWeight: 800,
    color: resolvedTheme.headerColor,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    marginBottom: 8,
  };
}

export function getSidebarItemStyle(theme, options = {}) {
  const resolvedTheme = getSidebarTheme(theme);
  const { active = false } = options;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 14px',
    cursor: 'pointer',
    borderRadius: 8,
    margin: '1px 6px',
    background: active ? resolvedTheme.activeBg : 'transparent',
    borderLeft: active ? `3px solid ${resolvedTheme.activeColor}` : '3px solid transparent',
    transition: 'all 120ms',
  };
}

export function getSidebarItemLabelStyle(theme, options = {}) {
  const resolvedTheme = getSidebarTheme(theme);
  const { active = false } = options;
  return {
    fontSize: 12.5,
    fontWeight: active ? 700 : 500,
    color: active ? resolvedTheme.activeColor : resolvedTheme.ink2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

export function getSidebarItemCountStyle(theme, options = {}) {
  const resolvedTheme = getSidebarTheme(theme);
  const { active = false } = options;
  return {
    fontSize: 11,
    fontWeight: 700,
    color: active ? resolvedTheme.activeColor : resolvedTheme.muted,
    background: active ? resolvedTheme.activeBg : resolvedTheme.bg,
    borderRadius: 99,
    padding: '1px 7px',
    minWidth: 22,
    textAlign: 'center',
    flexShrink: 0,
  };
}

export function getSidebarDividerStyle(theme) {
  const resolvedTheme = getSidebarTheme(theme);
  return {
    margin: '12px 14px 6px',
    borderTop: `1px solid ${resolvedTheme.border}`,
  };
}

export default {
  SIDEBAR_THEME,
  getSidebarTheme,
  getSidebarContainerStyle,
  getSidebarHeaderStyle,
  getSidebarHeaderLabelStyle,
  getSidebarItemStyle,
  getSidebarItemLabelStyle,
  getSidebarItemCountStyle,
  getSidebarDividerStyle,
};
